import { isBefore } from 'owa-datetime';
import { lazyEmptyFolder } from 'owa-folder-emptyfolder';
import folderStore from 'owa-folders';
import { MailFolder } from 'owa-graph-schema';
import * as Schema from 'owa-graph-schema';
import getConversationIdsFromConversations from 'owa-mail-list-store/lib/utils/getConversationIdsFromConversations';
import getItemIdsFromConversations from 'owa-mail-list-store/lib/utils/getItemIdsFromConversations';
import getItemidsFromMessages from 'owa-mail-list-store/lib/utils/getItemIdsFromMessages';
import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';
import { action } from 'satcheljs/lib/legacy';
import {
    getFocusedFilterForTable,
    getViewFilterForTable,
    TableView,
    isFolderPaused,
    listViewStore,
    getListViewTypeForFolderV2,
} from 'owa-mail-list-store';
import {
    ActionSource,
    isDeletedItemsFolderFromAnyMailbox,
    isDumpsterFolderFromAnyMailbox,
    isUnderDeletedItemsFolderFromAnyMailbox,
} from 'owa-mail-store';
import {
    PRIMARY_DELETED_ITEMS_DISTINGUISHED_ID,
    ARCHIVE_DELETED_ITEMS_DISTINGUISHED_ID,
} from 'owa-folders-constants';
import clearTableViewLastEmptiedTime from 'owa-mail-triage-action/lib/actions/clearTableViewLastEmptiedTime';

export interface EmptyTableViewState {
    folderToBeEmptied: MailFolder;
}

export default action('emptyTableView')(async function emptyTableView(
    tableViewToBeEmptied: TableView,
    isExplicitSoftDelete: boolean,
    actionSource: ActionSource,
    rowKeysToExclude: string[],
    state: EmptyTableViewState = {
        folderToBeEmptied: folderStore.folderTable.get(tableViewToBeEmptied.tableQuery.folderId),
    }
) {
    const { folderToBeEmptied } = state;
    const { selectAllModeTimeStamp, id, serverFolderId, totalRowsInView } = tableViewToBeEmptied;
    let deletedItemsFolderId;
    if (folderToBeEmptied.mailboxInfo.type == 'ArchiveMailbox') {
        deletedItemsFolderId = folderNameToId(ARCHIVE_DELETED_ITEMS_DISTINGUISHED_ID);
    } else {
        deletedItemsFolderId = folderNameToId(PRIMARY_DELETED_ITEMS_DISTINGUISHED_ID);
    }
    const isUnderDeletedItemsFolder = isUnderDeletedItemsFolderFromAnyMailbox(folderToBeEmptied);
    const listViewType = getListViewTypeForFolderV2(folderToBeEmptied);
    let clientLastSyncTime;
    let folder = folderToBeEmptied;
    // Use server folder id for empty table view if it's not deleteditems or junkemail or dumpster.
    // For deleteditems or junkemail or dumpster, they don't support delete all with a viewFilter specified.
    if (
        !(
            isDumpsterFolderFromAnyMailbox(folderToBeEmptied.FolderId.Id) ||
            isDeletedItemsFolderFromAnyMailbox(folderToBeEmptied.FolderId.Id) ||
            folderToBeEmptied.DistinguishedFolderId === 'junkemail'
        )
    ) {
        // Copy properties from folderToBeEmptied to folder
        folder = { ...folderToBeEmptied };
        folder.FolderId = <Schema.FolderId>{
            Id: serverFolderId,
        };
        folder.TotalCount = totalRowsInView;
        clientLastSyncTime = selectAllModeTimeStamp;
        if (isFolderPaused(folderToBeEmptied.FolderId.Id)) {
            const inboxPausedDate = listViewStore.inboxPausedDateTime;
            if (isBefore(inboxPausedDate, selectAllModeTimeStamp)) {
                clientLastSyncTime = inboxPausedDate;
            }
        }
    }
    let getConversationIdsToExclude;
    if (listViewType === ReactListViewType.Conversation) {
        getConversationIdsToExclude = () =>
            getConversationIdsFromConversations(rowKeysToExclude, id);
    }
    const getItemIdsToExclude = (): string[] => {
        let itemIdsToExclude: string[] = [];
        if (rowKeysToExclude.length > 0) {
            switch (listViewType) {
                case ReactListViewType.Conversation:
                    itemIdsToExclude = getItemIdsFromConversations(rowKeysToExclude, id);
                    break;
                case ReactListViewType.Message:
                    itemIdsToExclude = getItemidsFromMessages(
                        rowKeysToExclude,
                        tableViewToBeEmptied
                    );
                    break;
            }
        }
        return itemIdsToExclude;
    };
    await lazyEmptyFolder.importAndExecute(
        null, // emptyFolderMutation
        folder,
        getFocusedFilterForTable(tableViewToBeEmptied),
        getViewFilterForTable(tableViewToBeEmptied),
        isExplicitSoftDelete,
        actionSource,
        rowKeysToExclude,
        clientLastSyncTime,
        tableViewToBeEmptied.totalRowsInView,
        listViewType,
        deletedItemsFolderId,
        isUnderDeletedItemsFolder,
        id,
        getConversationIdsToExclude,
        getItemIdsToExclude
    );
    if (tableViewToBeEmptied) {
        clearTableViewLastEmptiedTime(tableViewToBeEmptied);
    }
});
